import { extend } from "@dhx/ts-common/core";
import { DataStore } from "@xbs/muon";
import { Actions, IAction, IActionsManager, IExecuteConfig, SpreadsheetEvents } from "../types";
import { AddColumn } from "./AddColumn";
import { AddRow } from "./AddRow";
import { AddSheet } from "./AddSheet";
import { DeleteColumn } from "./DeleteColumn";
import { DeleteRow } from "./DeleteRow";
import { DeleteSheet } from "./DeleteSheet";
import { GroupAction } from "./GroupAction";
import { GroupColAction } from "./GroupColAction";
import { GroupRowAction } from "./GroupRowAction";
import { LockCell } from "./LockCell";
import { RemoveCellStyles } from "./RemoveCellStyles";
import { RenameSheet } from "./RenameSheet";
import { SetCellFormat } from "./SetCellFormat";
import { SetCellStyle } from "./SetCellStyle";
import { SetCellValue } from "./SetCellValue";
import { Clear } from "./Clear";
import { ClearSheet } from "./ClearSheet";
import { ResizeCol } from "./ResizeCol";
import { ResizeRow } from "./ResizeRow";
import { IEventSystem } from "@dhx/ts-common/events";
import { cleanActionConfig } from "../helpers/main";
import { SetValidation } from "./SetValidation";
import { SortCells } from "./SortCells";
import { InsertLink } from "./InsertLink";
import { FitColumn } from "./FitCol";
import { Filter } from "./Filter";
import { Merge } from "./Merge";
import { Unmerge } from "./Unmerge";

export const actions = {
	setCellStyle: SetCellStyle,
	setCellValue: SetCellValue,
	setCellFormat: SetCellFormat,
	removeCellStyles: RemoveCellStyles,
	lockCell: LockCell,
	deleteRow: DeleteRow,
	addRow: AddRow,
	deleteColumn: DeleteColumn,
	addColumn: AddColumn,
	groupAction: GroupAction,
	groupRowAction: GroupRowAction,
	groupColAction: GroupColAction,
	addSheet: AddSheet,
	deleteSheet: DeleteSheet,
	renameSheet: RenameSheet,
	clear: Clear,
	clearSheet: ClearSheet,
	resizeCol: ResizeCol,
	resizeRow: ResizeRow,
	setValidation: SetValidation,
	sortCells: SortCells,
	insertLink: InsertLink,
	fitColumn: FitColumn,
	filter: Filter,
	merge: Merge,
	unmerge: Unmerge
};

function getActionsPack(commandsPack: IExecuteConfig[], baseConfig: any) {
	const actionsPack = commandsPack.map(config => {
		const action = config.groupAction || config.action;
		if (action) {
			return new (actions[action] as any)({ ...config, ...baseConfig });
		}
	});

	return {
		do() {
			actionsPack.map(action => action.do());
		},
		undo() {
			actionsPack.map(action => action.undo());
		},
		config: baseConfig
	};
}

export class ActionsManager implements IActionsManager {
	private _actions: {command: Actions, action: IAction}[];
	private _redoActions:  {command: Actions, action: IAction}[];
	private _config: any;
	private _dataStore: DataStore;
	private _sheets: string[];
	private _redoSheets: string[];
	private _events: IEventSystem<SpreadsheetEvents>;

	constructor(config: any) {
		this._config = config;
		this._actions = [];
		this._redoActions = [];
		this._sheets = [];
		this._redoSheets = [];
		this._dataStore = config.dataStore;
		this._events = config.events;
	}

	execute(command: Actions | IExecuteConfig[], config?: IExecuteConfig) {
		if (Array.isArray(command)) {
			const actionsPack = getActionsPack(command, this._config);
			actionsPack.do();
			this._actions.push({command: command[0].action, action: actionsPack});
			this._sheets.push(this._config.spreadsheet.activeSheetName);
			return;
		}
		extend(config, this._config);
		if (actions[command]) {
			const action = new (actions[command] as any)(config);
			if (!this._events.fire(SpreadsheetEvents.beforeAction, [command, cleanActionConfig(config)])) return;
			action.do();
			if (
				config.action === Actions.addSheet ||
				config.action === Actions.deleteSheet ||
				config.action === Actions.renameSheet
			) {
				this._sheets.push(null);
			} else {
				this._sheets.push(this._config.spreadsheet.activeSheetName);
			}
			this._actions.push({command, action});

			if (command !== Actions.resizeCol && command !== Actions.resizeRow) {
				this._events.fire(SpreadsheetEvents.afterAction, [command, cleanActionConfig(config)])
			}
		}
	}

	undo() {
		const item = this._actions[this._actions.length - 1];
		if (item) {
			const {command, action} = item;
			if (!this._events.fire(SpreadsheetEvents.beforeAction, [command, cleanActionConfig(action.config), true])) return;
			this._actions.pop();
			action.undo();
			const sheet = this._sheets.pop();
			if (sheet) {
				const activePage = this._dataStore.getPage(sheet);
				(this._config.spreadsheet as any)._changeSheet(activePage.getMeta().toolbarId);
			}
			this._redoActions.push({command, action});
			this._redoSheets.push(sheet);
			if (command !== Actions.resizeCol && command !== Actions.resizeRow) {
				this._events.fire(SpreadsheetEvents.afterAction, [command, cleanActionConfig(action.config)])
			}
		}
	}

	redo() {
		const item = this._redoActions[this._redoActions.length - 1];
		if (item) {
			const {command, action} = item;
			if (!this._events.fire(SpreadsheetEvents.beforeAction, [command, cleanActionConfig(action.config), true])) return;
			this._redoActions.pop();
			action.do();
			const sheet = this._redoSheets.pop();
			if (sheet) {
				const activePage = this._dataStore.getPage(sheet);
				(this._config.spreadsheet as any)._changeSheet(activePage.getMeta().toolbarId);
			}
			this._actions.push({command, action});
			this._sheets.push(sheet);
			if (command !== Actions.resizeCol && command !== Actions.resizeRow) {
				this._events.fire(SpreadsheetEvents.afterAction, [command, cleanActionConfig(action.config)])
			}
		}
	}
}
